import { Component, type ErrorInfo, type ReactNode } from 'react'
import { isChunkLoadError } from '@/lib/chunkLoadError'
import { reloadWithUpdateSplash } from '@/lib/updateReloadSplash'

interface AppErrorBoundaryProps {
  children: ReactNode
  /** Opens {@link VersionCheckDialog} with stale-chunk copy (user-initiated reload). */
  onChunkLoadError?: () => void
}

interface AppErrorBoundaryState {
  hasError: boolean
  error: Error | null
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  constructor(props: AppErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false, error: null }
  }

  static getDerivedStateFromError(error: Error): AppErrorBoundaryState {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('[AppErrorBoundary] Caught:', error, errorInfo)
    if (isChunkLoadError(error)) {
      this.props.onChunkLoadError?.()
    }
  }

  render() {
    const { hasError, error } = this.state
    if (!hasError || !error) {
      return this.props.children
    }

    // Chunk failures: VersionCheckDialog (parent) handles the prompt; keep a neutral backdrop.
    if (isChunkLoadError(error)) {
      return <div className="min-h-screen bg-background" aria-hidden="true" />
    }

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <div className="max-w-sm text-center space-y-4">
          <h1 className="text-xl font-semibold text-foreground">Something went wrong</h1>
          <p className="text-base text-muted-foreground leading-relaxed">
            An error has occurred. Please refresh and try again.
          </p>
          <button
            type="button"
            onClick={() => reloadWithUpdateSplash()}
            className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 rounded-lg transition-colors"
          >
            Refresh
          </button>
        </div>
      </div>
    )
  }
}
